<script setup lang="ts">
import OraInput from '@/components/common/form/OraInput.vue'
/**
 * I promemoria personali: elenco, aggiunta, modifica, spunta, eliminazione.
 *
 * Vive in due posti — il widget «Da fare» della dashboard e il pannello del pulsante flottante
 * «Utilità» — ed è un componente solo perché due copie divergerebbero al primo ritocco.
 *
 * Carica tutto in una chiamata (aperti + fatti) e li divide qui: sono note personali, poche
 * decine al massimo, e così il contatore dei fatti è disponibile senza un secondo giro.
 *
 * Sicurezza: nulla da fare qui. Il proprietario non è mai un parametro — il backend lo prende dal
 * token, e un id altrui risponde 404.
 */
import { ref, computed, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDaFare } from '@/composables/useDaFare'
import type { Promemoria } from '@/composables/useDaFare'

withDefaults(defineProps<{
  /** Nella dashboard il titolo lo dà già il widget; nel pannello «Utilità» serve. */
  mostraTitolo?: boolean
}>(), { mostraTitolo: false })

const { t } = useI18n()
const api = useDaFare()

const tutti = ref<Promemoria[]>([])
const caricamento = ref(true)
const salvataggio = ref(false)

const aperti = computed(() => ordina(tutti.value.filter(p => !p.fatto)))
const fatti = computed(() => tutti.value.filter(p => p.fatto))

// Nuovo
const nuovoTesto = ref('')
const nuovaScadenza = ref<Date | null>(null)
// Ora facoltativa: PrimeVue timeOnly produce comunque un Date, si estrae solo HH:mm
const nuovaOra = ref<Date | null>(null)

// Modifica in riga: id del promemoria aperto in edit, con i suoi valori di lavoro
const inModifica = ref<number | null>(null)
const testoModifica = ref('')
const scadenzaModifica = ref<Date | null>(null)
const oraModifica = ref<Date | null>(null)
const inputModifica = ref<{ $el?: HTMLElement } | null>(null)

const mostraFatti = ref(false)

onMounted(carica)

async function carica() {
  caricamento.value = true
  try {
    tutti.value = await api.elenco(true)
  } catch {
    tutti.value = []
  } finally {
    caricamento.value = false
  }
}

async function aggiungi() {
  const testo = nuovoTesto.value.trim()
  if (!testo || salvataggio.value) return
  salvataggio.value = true
  try {
    const creato = await api.crea(testo, toIsoDate(nuovaScadenza.value), toOra(nuovaOra.value))
    tutti.value = [...tutti.value, creato]
    nuovoTesto.value = ''
    nuovaScadenza.value = null
    nuovaOra.value = null
  } finally {
    salvataggio.value = false
  }
}

async function segna(p: Promemoria, fatto: boolean) {
  const agg = await api.segna(p.id, fatto)
  sostituisci(agg)
}

async function elimina(p: Promemoria) {
  await api.elimina(p.id)
  tutti.value = tutti.value.filter(x => x.id !== p.id)
  if (inModifica.value === p.id) annullaModifica()
}

// ── Modifica ──────────────────────────────────────────────────────────

async function apriModifica(p: Promemoria) {
  inModifica.value = p.id
  testoModifica.value = p.testo
  // La scadenza arriva come data civile aaaa-MM-gg: si costruisce il Date dai pezzi, mai da
  // `new Date('2026-08-10')` che è interpretata come UTC e può tornare indietro di un giorno.
  scadenzaModifica.value = daIsoDate(p.scadenza)
  oraModifica.value = daOra(p.scadenzaOra)
  await nextTick()
  inputModifica.value?.$el?.focus?.()
}

function annullaModifica() {
  inModifica.value = null
  testoModifica.value = ''
  scadenzaModifica.value = null
  oraModifica.value = null
}

async function salvaModifica(p: Promemoria) {
  const testo = testoModifica.value.trim()
  if (!testo || salvataggio.value) return
  salvataggio.value = true
  try {
    sostituisci(await api.modifica(p.id, testo, toIsoDate(scadenzaModifica.value), toOra(oraModifica.value)))
    annullaModifica()
  } finally {
    salvataggio.value = false
  }
}

// ── Utilità ───────────────────────────────────────────────────────────

function sostituisci(p: Promemoria) {
  tutti.value = tutti.value.map(x => (x.id === p.id ? p : x))
}

/** Aperti prima quelli con scadenza più vicina; quelli senza data in fondo. */
function ordina(list: Promemoria[]): Promemoria[] {
  return [...list].sort((a, b) => {
    if (a.scadenza && b.scadenza) return a.scadenza.localeCompare(b.scadenza)
    if (a.scadenza) return -1
    if (b.scadenza) return 1
    return b.id - a.id
  })
}

/** Date → aaaa-MM-gg dai campi locali: `toISOString()` convertirebbe in UTC e sposterebbe il giorno. */
function toIsoDate(d: Date | null): string | null {
  if (!d) return null
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const gg = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${gg}`
}

/** Date (timeOnly) → "HH:mm" dai campi locali: ora civile, nessun fuso. */
function toOra(d: Date | null): string | null {
  if (!d) return null
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** "HH:mm[:ss]" → Date odierna con quell'ora, per il DatePicker timeOnly. */
function daOra(hhmm: string | null): Date | null {
  if (!hhmm) return null
  const [h, m] = hhmm.split(':').map(Number)
  if (h === undefined || Number.isNaN(h)) return null
  const d = new Date()
  d.setHours(h, m ?? 0, 0, 0)
  return d
}

/** Adattatori OraInput («HH:mm» o null) ↔ Date dei form: il payload resta `toOra(Date)`, identico a prima. */
const nuovaOraTesto = computed<string | null>({
  get: () => toOra(nuovaOra.value),
  set: (v) => { nuovaOra.value = daOra(v) },
})
const oraModificaTesto = computed<string | null>({
  get: () => toOra(oraModifica.value),
  set: (v) => { oraModifica.value = daOra(v) },
})

/** aaaa-MM-gg → Date locale (mezzogiorno: immune a qualunque scherzo di ora legale). */
function daIsoDate(iso: string | null): Date | null {
  if (!iso) return null
  const [a, m, g] = iso.split('-').map(Number)
  if (!a || !m || !g) return null
  return new Date(a, m - 1, g, 12, 0, 0)
}

const oggiIso = computed(() => toIsoDate(new Date())!)

function etichettaData(iso: string | null, ora?: string | null): string {
  if (!iso) return ''
  const data = iso === oggiIso.value
    ? t('daFare.oggi', 'oggi')
    : (() => { const [a, m, g] = iso.split('-'); return `${g}/${m}/${a}` })()
  // L'ora arriva come HH:mm o HH:mm:ss: si mostra sempre HH:mm
  return ora ? `${data} ${ora.slice(0, 5)}` : data
}

function scaduto(iso: string | null, ora?: string | null): boolean {
  if (!iso) return false
  if (iso < oggiIso.value) return true
  if (iso > oggiIso.value) return false
  // Stesso giorno: è scaduto solo se ha un'ora ed è già passata
  if (!ora) return false
  const adesso = new Date()
  const hhmm = `${String(adesso.getHours()).padStart(2, '0')}:${String(adesso.getMinutes()).padStart(2, '0')}`
  return ora.slice(0, 5) < hhmm
}
</script>

<template>
  <section
    data-sezione="promemoria"
    class="pp"
  >
    <h4
      v-if="mostraTitolo"
      class="pp-titolo"
    >
      {{ t('daFare.promemoria', 'Promemoria') }}
    </h4>

    <div
      v-if="caricamento"
      class="pp-caricamento"
    >
      <i
        class="pi pi-spinner pi-spin"
        style="font-size: 1.1rem; color: var(--p-text-muted-color)"
      />
    </div>

    <template v-else>
      <ul
        v-if="aperti.length"
        class="pp-list"
      >
        <li
          v-for="p in aperti"
          :key="p.id"
          class="pp-riga"
        >
          <!-- Modifica in riga -->
          <template v-if="inModifica === p.id">
            <InputText
              ref="inputModifica"
              v-model="testoModifica"
              name="promemoriaTestoModifica"
              size="small"
              class="pp-edit-testo"
              maxlength="1000"
              @keyup.enter="salvaModifica(p)"
              @keyup.esc="annullaModifica"
            />
            <DatePicker
              v-model="scadenzaModifica"
              name="promemoriaScadenzaModifica"
              date-format="dd/mm/yy"
              show-icon
              icon-display="input"
              show-button-bar
              size="small"
              class="pp-edit-data"
              :placeholder="t('daFare.scadenzaOpz', 'Scadenza')"
              :manual-input="false"
            />
            <OraInput
              v-if="scadenzaModifica"
              v-model="oraModificaTesto"
              name="promemoriaOraModifica"
              class="pp-edit-ora"
            />
            <Button
              icon="pi pi-check"
              text
              rounded
              size="small"
              :disabled="!testoModifica.trim() || salvataggio"
              :aria-label="t('common.save', 'Salva')"
              @click="salvaModifica(p)"
            />
            <Button
              icon="pi pi-times"
              text
              rounded
              size="small"
              severity="secondary"
              :aria-label="t('common.cancel', 'Annulla')"
              @click="annullaModifica"
            />
          </template>

          <!-- Riga normale -->
          <template v-else>
            <Checkbox
              :model-value="p.fatto"
              binary
              @update:model-value="segna(p, true)"
            />
            <span
              class="pp-testo"
              :title="p.testo"
              @dblclick="apriModifica(p)"
            >{{ p.testo }}</span>
            <span
              v-if="p.scadenza"
              class="pp-data"
              :class="{ 'pp-data--scaduto': scaduto(p.scadenza, p.scadenzaOra) }"
            >
              {{ scaduto(p.scadenza, p.scadenzaOra) ? t('daFare.scaduto', 'scaduto') + ' · ' : '' }}{{ etichettaData(p.scadenza, p.scadenzaOra) }}
            </span>
            <Button
              icon="pi pi-pencil"
              text
              rounded
              size="small"
              class="pp-azione"
              :aria-label="t('daFare.modifica', 'Modifica')"
              @click="apriModifica(p)"
            />
            <Button
              icon="pi pi-times"
              text
              rounded
              size="small"
              severity="danger"
              class="pp-azione"
              :aria-label="t('daFare.elimina', 'Elimina')"
              @click="elimina(p)"
            />
          </template>
        </li>
      </ul>

      <!-- Nuovo promemoria -->
      <div
        id="field-promemoria-nuovo"
        class="pp-nuovo"
      >
        <InputText
          v-model="nuovoTesto"
          name="promemoriaTesto"
          :placeholder="t('daFare.nuovo', 'Scrivi un promemoria…')"
          size="small"
          class="pp-nuovo-testo"
          maxlength="1000"
          @keyup.enter="aggiungi"
        />
        <DatePicker
          v-model="nuovaScadenza"
          name="promemoriaScadenza"
          date-format="dd/mm/yy"
          show-icon
          icon-display="input"
          show-button-bar
          size="small"
          class="pp-nuovo-data"
          :placeholder="t('daFare.scadenzaOpz', 'Scadenza')"
          :manual-input="false"
        />
        <!-- L'ora compare solo quando c'è una data: un'ora senza giorno non significa niente -->
        <OraInput
          v-if="nuovaScadenza"
          v-model="nuovaOraTesto"
          name="promemoriaOra"
          class="pp-nuovo-ora"
        />
        <Button
          icon="pi pi-plus"
          size="small"
          :disabled="!nuovoTesto.trim() || salvataggio"
          :aria-label="t('daFare.aggiungi', 'Aggiungi')"
          @click="aggiungi"
        />
      </div>

      <!-- Fatti -->
      <div
        v-if="fatti.length"
        class="pp-fatti"
      >
        <a
          class="pp-link"
          @click.prevent="mostraFatti = !mostraFatti"
        >
          {{ mostraFatti ? t('daFare.nascondiFatti', 'Nascondi i fatti') : t('daFare.mostraFatti', 'Mostra i fatti') }}
          ({{ fatti.length }})
        </a>
        <ul
          v-if="mostraFatti"
          class="pp-list"
        >
          <li
            v-for="p in fatti"
            :key="p.id"
            class="pp-riga pp-riga--fatto"
          >
            <Checkbox
              :model-value="true"
              binary
              @update:model-value="segna(p, false)"
            />
            <span
              class="pp-testo"
              :title="p.testo"
            >{{ p.testo }}</span>
            <Button
              icon="pi pi-times"
              text
              rounded
              size="small"
              severity="danger"
              class="pp-azione"
              :aria-label="t('daFare.elimina', 'Elimina')"
              @click="elimina(p)"
            />
          </li>
        </ul>
      </div>
    </template>
  </section>
</template>

<style scoped>
.pp {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.pp-titolo {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
}

.pp-caricamento {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.pp-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.pp-riga {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem 0.25rem;
}

.pp-testo {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.825rem;
  color: var(--p-text-color);
  overflow-wrap: anywhere;
  cursor: text;
}

.pp-riga--fatto .pp-testo {
  text-decoration: line-through;
  color: var(--p-text-muted-color);
}

.pp-data {
  font-size: 0.7rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
  flex: 0 0 auto;
}
.pp-data--scaduto {
  color: var(--p-red-500);
  font-weight: 600;
}

/* Le azioni restano smorzate finché non si passa sulla riga: un elenco di note non deve
   sembrare una barra di pulsanti. */
.pp-azione {
  flex: 0 0 auto;
  opacity: 0.45;
}
.pp-riga:hover .pp-azione {
  opacity: 1;
}

.pp-edit-testo {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.825rem;
}

.pp-edit-data {
  flex: 0 0 8.5rem;
}
.pp-edit-data :deep(input) {
  font-size: 0.8rem;
}

.pp-nuovo {
  display: flex;
  gap: 0.375rem;
  align-items: center;
  margin-top: 0.25rem;
}

.pp-nuovo-testo {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.825rem;
}

.pp-nuovo-data {
  flex: 0 0 8.5rem;
}
.pp-nuovo-data :deep(input) {
  font-size: 0.8rem;
}

.pp-nuovo-ora,
.pp-edit-ora {
  flex: 0 0 auto;
}
.pp-nuovo-ora :deep(input),
.pp-edit-ora :deep(input) {
  font-size: 0.8rem;
}

.pp-fatti {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.pp-link {
  font-size: 0.775rem;
  color: var(--p-primary-color);
  cursor: pointer;
  text-decoration: none;
  align-self: flex-start;
}
.pp-link:hover {
  text-decoration: underline;
}
</style>
